import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  FileSpreadsheet, Presentation, ListChecks, BookOpen, LayoutTemplate, Map,
  type LucideIcon,
} from "lucide-react";
import { useTheme } from "@/providers/ThemeProvider";
import { palettes, resolveVariant, type DVariantKey } from "./covers/palette";

const variants = {
  d1: lazy(() => import("./covers/D1")),
  d2: lazy(() => import("./covers/D2")),
  d3: lazy(() => import("./covers/D3")),
  d4: lazy(() => import("./covers/D4")),
  d5: lazy(() => import("./covers/D5")),
  d6: lazy(() => import("./covers/D6")),
} as const;

const variantIcons: Record<DVariantKey, LucideIcon> = {
  d1: FileSpreadsheet,
  d2: Presentation,
  d3: ListChecks,
  d4: BookOpen,
  d5: LayoutTemplate,
  d6: Map,
};

export type DownloadCoverProps = {
  slug: string;
  title: string;
  description?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
};

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mql.matches);
    const onChange = () => setReduce(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduce;
}

export function DownloadCover({ slug, title, description, size = "sm", className }: DownloadCoverProps) {
  const { resolvedTheme } = useTheme();
  const reduce = usePrefersReducedMotion();
  const variantKey = useMemo(() => resolveVariant(slug), [slug]);
  const Variant = variants[variantKey];
  const Icon = variantIcons[variantKey];
  const palette = palettes[variantKey][resolvedTheme === "dark" ? "dark" : "light"];
  const uid = useMemo(() => `${variantKey}-${slug.replace(/[^a-z0-9]/gi, "")}`, [variantKey, slug]);

  const aspect = size === "lg" ? "aspect-[21/9]" : "aspect-video";

  return (
    <div
      className={`group/cover relative w-full overflow-hidden rounded-t-xl ${aspect} ${className ?? ""}`}
      style={{ background: `linear-gradient(135deg, ${palette.gradStart}, ${palette.gradEnd})` }}
    >
      <Suspense fallback={null}>
        <Variant title={title} description={description} palette={palette} animate={!reduce} uid={uid} />
      </Suspense>
      <div className="pointer-events-none absolute bottom-3 left-3 flex h-10 w-10 items-center justify-center rounded-xl border border-white/30 bg-white/15 text-white shadow-sm backdrop-blur-md">
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-gradient-to-b from-transparent to-card" />
    </div>
  );
}

export default DownloadCover;
